import { Box, Skeleton, Stack } from '@mui/material';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import BrandDisplay from '/@/components/brand-display';
import FieldInfo from '/@/components/field-info';
import HeadToHeadInfo from '/@/components/head-to-head-info';
import PrivacyBar from '/@/components/privacy-bar';
import PrivacyDisclaimer from '/@/components/privacy-disclaimer';
import Spinner from '/@/components/spinner';
import { useFieldById } from '/@/hooks/fields';
import { useH2H, useH2HLogCreateMutation } from '/@/hooks/h2h';
import { _ } from '/@/locale';
import {
    getCornHeadToHeadEnabled,
    getSessionBrands,
    getSoyHeadToHeadEnabled,
    getUser,
} from '/@/store/session/selectors';
import { isMobile } from '/@/utils';
import ErrorView from '/@/views/error-view';
import CornConfiguration from '../components/modules/corn-configuration';
import Humidity from '../components/modules/humidity';
import LinearRegression from '../components/modules/linear-regression';
import Yield from '../components/modules/yield';
import styles from './styles.module.css';

const HeadToHeadLayout = ({ brand, crop, field, zone, showZone, environment, head, loading, children }) => {
    return (
        <Box className={styles.root}>
            <PrivacyBar />
            {loading ? (
                <Box height='100%' width='100%' display='flex' alignItems='center' justifyContent='center'>
                    <Spinner />
                </Box>
            ) : (
                <Box className={styles.contentContainer}>
                    <Box className={styles.sidebar}>
                        <BrandDisplay brand={brand} borderRadius={false} />
                        {/* TODO: BRAND */}
                        <FieldInfo shouldCollapse field={field} />
                        <HeadToHeadInfo
                            crop={crop}
                            zone={zone}
                            showZone={showZone}
                            environment={environment}
                            head={head}
                        />
                    </Box>

                    <Box className={styles.content}>
                        {!zone ? (
                            <Alert severity='danger'>{_('No hay datos disponibles para el lote seleccionado.')}</Alert>
                        ) : (
                            children
                        )}
                    </Box>
                </Box>
            )}
            <PrivacyDisclaimer crop={crop} warning={_('Por favor no compartir con terceros.')} />
        </Box>
    );
};

function CornHeadToHead({ crop, fieldId }) {
    const user = useSelector(getUser);
    const sessionBrands = useSelector(getSessionBrands);
    const brand = sessionBrands[crop][0]; // TODO: BRANDS

    const fieldQuery = useFieldById({ id: fieldId });
    const zoneIds = fieldQuery.data?.node?.zones?.map((z) => z.id) || [];

    const {
        zone,
        setZone,

        head,
        setHead,

        environment,
        setEnvironment,

        yieldBrands,
        setYieldBrands,

        humidityBrands,
        setHumidityBrands,

        otherForLinearRegression,
        setOtherForLinearRegression,

        environments,
        results,
        zoneOptions,
        headOptions,
        yieldBrandOptions,
        humidityBrandOptions,
        yieldTrials,
        others,

        filteredYieldTable,
        filteredHumidityTable,

        isValidating,
        errors,
    } = useH2H({ crop, brand, zoneIds, altitude: fieldQuery.data?.altitude });

    // H2H Log
    const H2HLogCreateMutation = useH2HLogCreateMutation();
    useEffect(() => {
        if (head)
            H2HLogCreateMutation.trigger({
                cornHead: head.id,
            });
    }, [head]);

    const renderConfiguration = () => {
        return (
            <CornConfiguration
                zone={zone}
                zoneOptions={zoneOptions}
                setZone={setZone}
                environment={environment || { min: 0, max: 0 }}
                environments={environments}
                environmentType={environments ? environments.type : undefined}
                setEnvironment={setEnvironment}
                head={head}
                headOptions={headOptions}
                setHead={setHead}
            />
        );
    };

    const renderError = () => {
        if (!head || (results && !!results.yieldTable.length) || Object.values(isValidating).some(Boolean)) return null;

        return (
            <Alert severity='danger' sx={{ mt: 2 }}>
                {_('No hay datos disponibles para los filtros seleccionados.')}
            </Alert>
        );
    };

    const renderYield = () => {
        if (isValidating.results)
            return (
                <Stack spacing={1} my={5}>
                    <Skeleton variant='rounded' animation='wave' height='50px' />
                    <Skeleton variant='rounded' animation='wave' height='50px' />
                    <Skeleton variant='rounded' animation='wave' height='50px' />
                    <Skeleton variant='rounded' animation='wave' height='50px' />
                </Stack>
            );
        if (!head || !yieldBrandOptions.length || !environment) return null;
        return (
            <Yield
                crop={crop}
                title=''
                user={user}
                headToHeads={filteredYieldTable}
                brands={yieldBrands}
                brandOptions={yieldBrandOptions}
                setBrands={setYieldBrands}
                onLinearRegressionClick={handleLinearRegressionClick}
            />
        );
    };

    const renderHumidity = () => {
        if (!head || !humidityBrandOptions.length) return null;

        return (
            <Humidity
                user={user}
                headToHeads={filteredHumidityTable}
                brands={humidityBrands}
                brandOptions={humidityBrandOptions}
                setBrands={setHumidityBrands}
            />
        );
    };

    const renderLinearRegression = () => {
        if (!yieldTrials || !environments || !otherForLinearRegression || isValidating.results || !others.length)
            return null;

        return (
            <LinearRegression
                crop={crop}
                user={user}
                headToHead={yieldTrials}
                other={otherForLinearRegression}
                otherOptions={others}
                setOther={(otherId) => setOtherForLinearRegression(others.find(({ id }) => id === otherId))}
                type={environments.type}
            />
        );
    };

    const handleLinearRegressionClick = (other) => {
        setOtherForLinearRegression(other);

        // Hackish way to scroll content to bottom to show linear regression. Try to find a cleaner way.
        const content = document.querySelector(`.${isMobile() ? styles.root : styles.content}`);
        content.scrollTo({ top: content.scrollHeight, behavior: 'smooth' });
    };

    return (
        <HeadToHeadLayout
            field={fieldQuery.data}
            crop={crop}
            zone={zone}
            showZone={zoneOptions.length > 1}
            environment={environment || { min: 0, max: 0 }}
            head={head}
            brand={brand}
            loading={isValidating.h2hOptions}
        >
            {renderConfiguration()}
            {renderError()}
            {renderYield()}
            {renderHumidity()}
            {renderLinearRegression()}
        </HeadToHeadLayout>
    );
}

function SoyHeadToHead({ crop, fieldId }) {
    const user = useSelector(getUser);
    const sessionBrands = useSelector(getSessionBrands);
    const brand = sessionBrands[crop][0]; // TODO: BRANDS

    const fieldQuery = useFieldById({ id: fieldId });
    const zoneIds = fieldQuery.data?.node?.zones?.map((z) => z.id) || [];

    const {
        zone,
        setZone,

        head,
        setHead,

        environment,
        setEnvironment,

        yieldBrands,
        setYieldBrands,

        otherForLinearRegression,
        setOtherForLinearRegression,

        environments,
        results,
        zoneOptions,
        headOptions,
        yieldBrandOptions,
        yieldTrials,
        others,

        filteredYieldTable,

        isValidating,
        errors,
    } = useH2H({ crop, brand, zoneIds, altitude: fieldQuery.data?.altitude });

    // H2H Log
    const H2HLogCreateMutation = useH2HLogCreateMutation();
    useEffect(() => {
        if (head)
            H2HLogCreateMutation.trigger({
                soyHead: head.id,
            });
    }, [head]);

    const renderConfiguration = () => {
        return (
            <CornConfiguration
                zone={zone}
                zoneOptions={zoneOptions}
                setZone={setZone}
                environment={environment || { min: 0, max: 0 }}
                environments={environments}
                environmentType={environments ? environments.type : undefined}
                setEnvironment={setEnvironment}
                head={head}
                headOptions={headOptions}
                setHead={setHead}
            />
        );
    };

    const renderError = () => {
        if (!head || (results && !!results.yieldTable.length) || Object.values(isValidating).some(Boolean)) return null;

        return (
            <Alert severity='danger' sx={{ mt: 2 }}>
                {_('No hay datos disponibles para los filtros seleccionados.')}
            </Alert>
        );
    };

    const renderYield = () => {
        if (isValidating.results)
            return (
                <Stack spacing={1} my={5}>
                    <Skeleton variant='rounded' animation='wave' height='50px' />
                    <Skeleton variant='rounded' animation='wave' height='50px' />
                    <Skeleton variant='rounded' animation='wave' height='50px' />
                    <Skeleton variant='rounded' animation='wave' height='50px' />
                </Stack>
            );
        if (!head || !yieldBrandOptions.length || !environment) return null;
        return (
            <Yield
                crop={crop}
                title=''
                user={user}
                headToHeads={filteredYieldTable}
                brands={yieldBrands}
                brandOptions={yieldBrandOptions}
                setBrands={setYieldBrands}
                onLinearRegressionClick={handleLinearRegressionClick}
            />
        );
    };

    const renderLinearRegression = () => {
        if (!yieldTrials || !environments || !otherForLinearRegression || isValidating.results || !others.length)
            return null;

        return (
            <LinearRegression
                crop={crop}
                user={user}
                headToHead={yieldTrials}
                other={otherForLinearRegression}
                otherOptions={others}
                setOther={(otherId) => setOtherForLinearRegression(others.find(({ id }) => id === otherId))}
                type={environments.type}
            />
        );
    };

    const handleLinearRegressionClick = (other) => {
        setOtherForLinearRegression(other);

        // Hackish way to scroll content to bottom to show linear regression. Try to find a cleaner way.
        const content = document.querySelector(`.${isMobile() ? styles.root : styles.content}`);
        content.scrollTo({ top: content.scrollHeight, behavior: 'smooth' });
    };

    return (
        <HeadToHeadLayout
            field={fieldQuery?.data}
            crop={crop}
            zone={zone}
            showZone={zoneOptions.length > 1}
            environment={environment || { min: 0, max: 0 }}
            head={head}
            brand={brand}
            loading={isValidating.h2hOptions}
        >
            {renderConfiguration()}
            {renderError()}
            {renderYield()}
            {renderLinearRegression()}
        </HeadToHeadLayout>
    );
}

const HeadToHead = ({ fieldId, crop }) => {
    const fieldQuery = useFieldById({ id: fieldId });
    const brands = useSelector(getSessionBrands);

    const validCrop = fieldQuery.data?.node.crops.filter((crop) => brands[crop]).includes(crop); // TODO: BRAND

    const cornHeadToHeadEnabled = useSelector(getCornHeadToHeadEnabled);
    const soyHeadToHeadEnabled = useSelector(getSoyHeadToHeadEnabled);

    if (fieldQuery.isValidating)
        return (
            <Stack justifyContent='center' alignItems='center'>
                <Spinner />
            </Stack>
        );
    if (!fieldQuery.data || !validCrop) return <ErrorView />;

    if (cornHeadToHeadEnabled && crop === 'corn') return <CornHeadToHead fieldId={fieldId} crop={crop} />;
    if (soyHeadToHeadEnabled && crop === 'soy') return <SoyHeadToHead fieldId={fieldId} crop={crop} />;
    return <ErrorView />;
};

export default HeadToHead;
